/**
 * Computes what `handle-admin-approval` will pay (or why it will refuse) for a job's final
 * release, using the same core arithmetic (`tradeGstCents`, `computeFinalReleaseBase`,
 * `computeHoldbackAdjustment`) as the edge function.
 *
 * Web / mobile helper only — do NOT import this module from Deno edge functions.
 */

import { tradeGstCents, type TradeCompanyGstInput } from '../core/financial';
import {
  computeHoldbackAdjustment,
  type HoldbackAdjustment,
  type HoldbackPayoutRow,
} from '../core/holdbackAdjustment';
import {
  computeFinalReleaseBase,
  hasTradeShareSnapshot,
  type FinalReleaseBase,
  type FinalReleaseCompletedRow,
  type FinalReleaseInspectorSplitRow,
} from '../core/payoutAmount';

export type FinalReleaseOwedCompletedRow = FinalReleaseCompletedRow & HoldbackPayoutRow;

export interface FinalReleasePendingRow {
  id?: string | null;
  transaction_type?: string | null;
  status?: string | null;
}

export interface ComputeFinalReleaseOwedInput {
  tradeNetCents: number | null | undefined;
  clientTotalCents?: number | null;
  completedAt?: Date | string | null;
  trade?: TradeCompanyGstInput | null;
  completedRows?: readonly FinalReleaseOwedCompletedRow[] | null;
  inspectorSplitRows?: readonly FinalReleaseInspectorSplitRow[] | null;
  pendingRows?: readonly FinalReleasePendingRow[] | null;
}

export type FinalReleaseOwedResult =
  | { kind: 'MISSING_SNAPSHOT' }
  | { kind: 'INITIAL_PENDING' }
  | { kind: 'FINAL_IN_FLIGHT' }
  | {
      kind: 'OWED';
      finalReleaseCents: number;
      finalReleaseGstCents: number;
      holdbackAdjustment: HoldbackAdjustment;
      base: FinalReleaseBase;
    };

const INITIAL_PAYOUT_TRANSACTION_TYPES = new Set([
  'INITIAL_RELEASE',
  'INITIAL_PAYOUT',
  'ON_SITE_INITIAL_RELEASE',
]);

function isPendingOrProcessingInitialRow(row: FinalReleasePendingRow): boolean {
  if (!row.transaction_type || !INITIAL_PAYOUT_TRANSACTION_TYPES.has(row.transaction_type)) {
    return false;
  }
  return row.status == null || row.status === 'PENDING' || row.status === 'PROCESSING';
}

function isPendingOrProcessingFinalRow(row: FinalReleasePendingRow): boolean {
  if (row.transaction_type !== 'FINAL_RELEASE') {
    return false;
  }
  return row.status == null || row.status === 'PENDING' || row.status === 'PROCESSING';
}

export function computeFinalReleaseOwed({
  tradeNetCents,
  clientTotalCents,
  completedAt,
  trade,
  completedRows,
  inspectorSplitRows,
  pendingRows,
}: ComputeFinalReleaseOwedInput): FinalReleaseOwedResult {
  if (!hasTradeShareSnapshot(tradeNetCents, clientTotalCents)) {
    return { kind: 'MISSING_SNAPSHOT' };
  }

  if ((pendingRows ?? []).some(isPendingOrProcessingInitialRow)) {
    return { kind: 'INITIAL_PENDING' };
  }

  if ((pendingRows ?? []).some(isPendingOrProcessingFinalRow)) {
    return { kind: 'FINAL_IN_FLIGHT' };
  }

  const gstCents = tradeGstCents(
    tradeNetCents,
    trade,
    completedAt ? new Date(completedAt) : new Date(),
  );

  const base = computeFinalReleaseBase({
    tradeNetCents,
    tradeGstCents: gstCents,
    completedRows,
    inspectorSplitRows,
  });

  const holdbackAdjustment = computeHoldbackAdjustment({
    holdbackCents: base.holdbackCents,
    holdbackGstCents: base.holdbackGstCents,
    rows: completedRows ?? [],
  });

  return {
    kind: 'OWED',
    finalReleaseCents: holdbackAdjustment.final_release_cents,
    finalReleaseGstCents: holdbackAdjustment.final_release_gst_cents,
    holdbackAdjustment,
    base,
  };
}
